import { Checkbox, FormControlLabel } from "@mui/material";
import React, { useCallback, useEffect } from "react";
import { useActionRequest } from "../../../../../../common/hooks/api";
import { Api } from "../../../../../../common/service/Api";
import { usePrimaryOrganization } from "../../../../../hooks/useOrganization";
import { SUBMIT_FIELD_NAMES } from "../../../DataRequestSubjectCard";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { Link } from "react-router-dom";

export const CompileProfilingInfo = () => {
  const [org] = usePrimaryOrganization();
  const { request, refreshRequest, setStepCompleted } = useDataRequestProcessingContext();

  const api = useCallback(
    ({ request, finished }) => {
      const existingValues = Object.fromEntries(
        Object.entries(request).filter(([k]) => SUBMIT_FIELD_NAMES.includes(k)),
      );
      const values = Object.assign({}, existingValues, {
        profilingInformationCompiled: finished,
      }) as any;
      return Api.dataRequest.updateRequest(org.id, request.id, values);
    },
    [request],
  );

  const { fetch: updateField } = useActionRequest({ api });

  const handleSelectionUpdated = async (isFinished: boolean) => {
    await updateField({ request, finished: isFinished });
    refreshRequest();
  };

  useEffect(() => {
    let isCompleted = false;
    if (request.profilingInformationCompiled) {
      isCompleted = true;
    }
    setStepCompleted(isCompleted);
  }, [request.consumerGroupResult, request.profilingInformationCompiled, setStepCompleted]);

  if (request.consumerGroupResult === "PROCESSED_AS_SERVICE_PROVIDER") {
    return (
      <>
        <div className="text-t3 mb-std">
          You have marked this request as being processed as a service provider. You may proceed to
          the next step to notify the consumer with information on how to exercise their rights.
        </div>
      </>
    );
  }

  return (
    <>
      <div className="text-t3 mb-std">
        Your business indicated in its{" "}
        <Link className="text-weight-regular" to={`/organization/${org.id}/settings/edit`}>
          Business Survey
        </Link>{" "}
        that it uses automated profiling to make decisions having legal or similarly significant
        effects on consumers. To respond to this consumer's request, compile the following
        information in a document. If you did not make any profiling decisions concerning this
        consumer, say so in the document. In the next step, we'll ask you to upload that document to
        an email you will send to the consumer.
      </div>

      <div className="text-t3 mb-std">
        <ul>
          <li>Provide the results of any profiling decision made about the consumer.</li>
          <li>Provide the reason that the profiling resulted in the decision.</li>
          <li>
            If feasible, describe the actions the consumer might have taken to secure a different
            decision and what actions the consumer might take to secure a different decision in the
            future.
          </li>
          <li>List what information about the consumer was used in the profiling.</li>
        </ul>
      </div>

      <FormControlLabel
        control={
          <Checkbox
            checked={request.profilingInformationCompiled ?? false}
            onChange={({ target }) => handleSelectionUpdated(target.checked)}
          />
        }
        label="I have compiled the information above in a document that I will upload and send to the consumer in the next step."
      />
    </>
  );
};
